import React, { useCallback, useContext, useState } from 'react';
import { GenericErrorMessage } from 'src/components/BeaconV2Components/common/CommonSnackbarMessages/GenericErrorMessage';
import ExportModal from 'src/components/Modals/ExportModal';
import { useAppSelector, useSnackbar } from 'src/utils/Hooks';
import { ExportCheckBox } from 'src/components/BeaconV2Components/common/AppExportModal/AppExportModal';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { ExportContext } from 'src/providers/ExportContextProvider';
import { useDorisApplyEntityFilters } from 'src/utils/Hooks/useDorisApplyEntityFilters';
import axios from 'axios';
import {
  useBusinessUnitEntity,
  useCategoryEntity,
  useMainEntity,
  useMainEntityType,
  useRetailerDisplayName,
  useRetailerId,
  useSegmentEntity
} from 'src/utils/Hooks/reduxSelectors';
import { EXPORT_TYPES, ExportType } from 'src/components/AtlasV2Components/AtlasDorisExportComponents/Consts';

const useDropdownOptions = ({ entityType }: { entityType: string }) => {
  const { isAllCategoriesEntity } = useCategoryEntity();
  switch (entityType) {
    case 'brand':
      return [
        { id: EXPORT_TYPES.PRODUCT, label: 'Product', fieldName: 'product' },
        { id: EXPORT_TYPES.CATEGORY, label: 'Category', fieldName: 'category' },
        { id: EXPORT_TYPES.SUBCATEGORY, label: 'Subcategory', fieldName: 'subcategory' },
        { id: EXPORT_TYPES.KEYWORD, label: 'Keyword', fieldName: 'keyword' },
        {
          id: EXPORT_TYPES.PRODUCT_KEYWORD,
          label: 'Product & Keyword',
          fieldName: 'productAndKeyword',
          excludeSeparateByWeek: true
        }
      ];
    case 'category':
      return [
        { id: EXPORT_TYPES.PRODUCT, label: 'Product', fieldName: 'product' },
        { id: EXPORT_TYPES.BRAND, label: 'Brand', fieldName: 'brand' },
        ...(isAllCategoriesEntity ? [{ id: EXPORT_TYPES.CATEGORY, label: 'Category', fieldName: 'category' }] : [{}]),
        { id: EXPORT_TYPES.SUBCATEGORY, label: 'Subcategory', fieldName: 'subcategory' },
        { id: EXPORT_TYPES.BRAND_SUBCATEGORY, label: 'Brand & Subcategory', fieldName: 'brandAndSubcategory' },
        { id: EXPORT_TYPES.KEYWORD, label: 'Keyword', fieldName: 'keyword' },
        {
          id: EXPORT_TYPES.BRAND_KEYWORD,
          label: 'Brand & Keyword',
          fieldName: 'brandAndKeyword',
          excludeSeparateByWeek: true
        },
        {
          id: EXPORT_TYPES.PRODUCT_KEYWORD,
          label: 'Product & Keyword',
          fieldName: 'productAndKeyword',
          excludeSeparateByWeek: true
        },
        {
          id: EXPORT_TYPES.SUBCATEGORY_KEYWORD,
          label: 'Subcategory & Keyword',
          fieldName: 'subcategoryAndKeyword',
          excludeSeparateByWeek: true
        }
      ];

    case 'subcategory':
      return [
        { id: EXPORT_TYPES.PRODUCT, label: 'Product', fieldName: 'product' },
        { id: EXPORT_TYPES.BRAND, label: 'Brand', fieldName: 'brand' },
        { id: EXPORT_TYPES.KEYWORD, label: 'Keyword', fieldName: 'keyword' },
        {
          id: EXPORT_TYPES.BRAND_KEYWORD,
          label: 'Brand & Keyword',
          fieldName: 'brandAndKeyword',
          excludeSeparateByWeek: true
        },
        {
          id: EXPORT_TYPES.PRODUCT_KEYWORD,
          label: 'Product & Keyword',
          fieldName: 'productAndKeyword',
          excludeSeparateByWeek: true
        }
      ];
    case 'product':
      return [
        { id: EXPORT_TYPES.PRODUCT, label: 'Product', fieldName: 'product' },
        { id: EXPORT_TYPES.KEYWORD, label: 'Keyword', fieldName: 'keyword' }
      ];
    default:
      return [
        { id: EXPORT_TYPES.PRODUCT, label: 'Product', fieldName: 'product' },
        { id: EXPORT_TYPES.BRAND, label: 'Brand', fieldName: 'brand' },
        { id: EXPORT_TYPES.CATEGORY, label: 'Category', fieldName: 'category' },
        { id: EXPORT_TYPES.SUBCATEGORY, label: 'Subcategory', fieldName: 'subcategory' },
        { id: EXPORT_TYPES.BRAND_SUBCATEGORY, label: 'Brand & Subcategory', fieldName: 'brandAndSubcategory' },
        { id: EXPORT_TYPES.KEYWORD, label: 'Keyword', fieldName: 'keyword' },
        {
          id: EXPORT_TYPES.BRAND_KEYWORD,
          label: 'Brand & Keyword',
          fieldName: 'brandAndKeyword',
          excludeSeparateByWeek: true
        },
        {
          id: EXPORT_TYPES.PRODUCT_KEYWORD,
          label: 'Product & Keyword',
          fieldName: 'productAndKeyword',
          excludeSeparateByWeek: true
        },
        {
          id: EXPORT_TYPES.SUBCATEGORY_KEYWORD,
          label: 'Subcategory & Keyword',
          fieldName: 'subcategoryAndKeyword',
          excludeSeparateByWeek: true
        }
      ];
  }
};

export type MetricType = 'totalClicks' | 'organicClicks' | 'paidClicks' | 'otherClicks';

export interface ExportConfiguration {
  /**
   * Type of export grouping
   */
  exportType: ExportType;

  fileName: string;

  columnDelimiter: string;

  requestSource: string;

  shouldGroupByWeek: boolean;

  metricType: MetricType;

  /**
   * Field name to group by
   */
  fieldName: string;

  retailerId: number;

  categoryIds?: number[];

  subcategoryIds?: string[];

  brandIds?: number[];

  segmentIds?: string[];

  keywords: string[];

  startWeekId: number;

  endWeekId: number;

  comparisonStartWeekId: number;

  comparisonEndWeekId: number;

  minRetailPrice?: number;

  maxRetailPrice?: number;
}

const AtlasV2DorisTableTrafficV2ExportModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const queryParams = useAppSelector((state) => state.app.queryParams);
  const [isSeparatedByWeek, setIsSeparatedByWeek] = useState(false);
  const [isIncludeComparison, setIsIncludeComparison] = useState(false);
  const { name: mainEntityName, entityType: mainEntityType } = useMainEntity();
  const dropdownOptions = useDropdownOptions({ entityType: mainEntityType });

  const type = useMainEntityType();
  const name = useMainEntity()?.name;
  const { subtab, sw, ew } = useAppSelector((state) => state.app.queryParams);
  const retailer = useRetailerDisplayName();

  // TODO: We have a hook useExportFileName that can be used to generate the export file name, but subtab is trafficV2, we should use useExportFileName when v1 is deprecated
  const exportFileName = `${type}_${name}_traffic_${subtab}_${retailer}_${sw}_${ew}`;

  const [selectedGroupBy, setSelectedGroupBy] = useState(dropdownOptions[0]);
  const { startPolling } = useContext(ExportContext);

  const retailerId = useRetailerId();
  const { showSnackbar } = useSnackbar();
  const dorisApplyEntityFilters = useDorisApplyEntityFilters();
  const { isSegmentEntity } = useSegmentEntity();
  const { isBusinessUnitEntity } = useBusinessUnitEntity();

  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);
  const { startWeek: startComparisonWeek, endWeek: endComparisonWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );

  const handleExportClick = useCallback(async () => {
    try {
      const exportParams = dorisApplyEntityFilters(
        {
          exportType: selectedGroupBy?.id,
          fieldName: selectedGroupBy?.fieldName,
          metricType: subtab,
          shouldGroupByWeek: isSeparatedByWeek && !selectedGroupBy?.excludeSeparateByWeek,
          retailerId: +retailerId,
          startWeekId: startWeek,
          endWeekId: endWeek,
          fileName: exportFileName,
          comparisonStartWeekId: isIncludeComparison ? startComparisonWeek : undefined,
          comparisonEndWeekId: isIncludeComparison ? endComparisonWeek : undefined,
          ...(isSegmentEntity || isBusinessUnitEntity ? { entityType: mainEntityType, entityName: mainEntityName } : {})
        },
        false
      );

      await axios.post('/api/atlas/SubmitExportRequest', exportParams);

      showSnackbar({ type: 'success', message: 'Your download is processing.' });
      startPolling();
      onClose();
    } catch (error) {
      console.error('Export failed:', error);
      showSnackbar({
        message: <GenericErrorMessage />,
        type: 'error'
      });
    }
  }, [
    dorisApplyEntityFilters,
    endComparisonWeek,
    endWeek,
    exportFileName,
    isIncludeComparison,
    isSeparatedByWeek,
    onClose,
    retailerId,
    selectedGroupBy?.fieldName,
    selectedGroupBy?.id,
    showSnackbar,
    startComparisonWeek,
    startPolling,
    startWeek,
    subtab
  ]);

  return (
    <ExportModal open={open} onClose={onClose}>
      <ExportModal.Header title="Export" onClose={onClose} />
      <ExportModal.Body
        isSeparatedByWeek={isSeparatedByWeek}
        isIncludeComparison={isIncludeComparison}
        setIsSeparatedByWeek={setIsSeparatedByWeek}
        setIsIncludeComparison={setIsIncludeComparison}
        customTimePeriodComponent={
          <>
            {!selectedGroupBy?.excludeSeparateByWeek && (
              <ExportCheckBox
                isSelected={isSeparatedByWeek}
                id="separateDataByWeek"
                label="Separate data by week"
                onSelect={(e) => {
                  setIsSeparatedByWeek(e.target.checked);
                }}
              />
            )}
            {/* Include comparison checkbox */}
            <ExportCheckBox
              isSelected={isIncludeComparison}
              id="includeComparison"
              label="Include comparison time period"
              onSelect={(e) => setIsIncludeComparison(e.target.checked)}
            />
          </>
        }
      >
        <SlDropdownMenu
          options={dropdownOptions}
          onChange={(option) => setSelectedGroupBy(option)}
          selectedId={selectedGroupBy.id}
        />
      </ExportModal.Body>
      <ExportModal.Footer
        searchParams={queryParams.searchParams}
        handleClose={onClose}
        handleExportClick={handleExportClick}
      />
    </ExportModal>
  );
};

export default AtlasV2DorisTableTrafficV2ExportModal;
